import { match, Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { CmsIndex } from '../utils/cmsIndex';
import { accountRouting } from './pages/AccountPage';
import { checkoutRouting } from './pages/CheckoutPage';
import { createAccountRouting } from './pages/CreateAccountPage';
import { editOrderRouting } from './pages/EditOrderPage';
import { homeRouting } from './pages/HomePage';
import { loginRouting } from './pages/LoginPage';
import { NotFoundPage, notFoundRouting } from './pages/NotFoundPage';
import { orderRouting } from './pages/OrderPage';
import { ordersRouting } from './pages/OrdersPage';
import { productRouting } from './pages/ProductPage';
import { reorderRouting } from './pages/ReorderPage';
import { subscriptionsRouting } from './pages/SubscriptionsPage';
/* import { categoryRouting } from './pages/CategoryPageWrapper'; */
/* import { categoryRouting } from './pages/CategoryPage'; */
import { Config } from '../config';
import { Translations, useTranslation } from '../i18n/i18n';
import { PreloadedData } from '../state/api';
import { useSession } from '../state/session';
import { ApiCategory } from '../types';
import { Main } from './Main/Main';
import { bonusRouting } from './pages/BonusPage';
import { brandRouting } from './pages/BrandPage';
import { brandsPageRouting } from './pages/BrandsPage';
import { campaignRouting } from './pages/CampaignPage';
import { categoryRouting } from './pages/CategoryPageLoop54';
import { checkoutThanksRouting, companyCheckoutThanksRouting } from './pages/CheckoutThanksPage';
import { cmsRouting } from './pages/CmsPage';
import { communicationRouting } from './pages/CommunicationPage';
import { confirmDeleteRouting } from './pages/ConfirmDeletePage';
import { deleteAccountRouting } from './pages/DeleteAccountPage';
import { exportStatisticsRouting } from './pages/ExportStatisticsPage';
import { favoritesRouting } from './pages/FavoritesPage';
import { forgotPasswordRouting } from './pages/ForgotPasswordPage';
import { myPagesRouting } from './pages/MyPages';
import { orderFulfillmentRouting } from './pages/OrderFulfillmentPage';
import { previouslyBoughtRouting } from './pages/PreviouslyBoughtPage';
import { printerModelRouting } from './pages/PrinterModelPage';
import { printerRouting } from './pages/PrinterPage';
import { productsAoRouting } from './pages/ProductsAoPage';
import { punchoutRouting } from './pages/PunchoutPage';
import { resetPasswordRouting } from './pages/ResetPasswordPage';
import { searchRouting } from './pages/SearchPageWrapper';
import { specialPricesRouting, specialPricesRouting2 } from './pages/SpecialPricesPage';
import { statisticsRouting } from './pages/StatisticsPage';
import { superCategoryRouting } from './pages/SuperCategoryPage';
import { switchAccountRouting } from './pages/SwitchAccountPage';
import { tonerGuideRouting } from './pages/TonerGuidePage';
export function parseQuery<T extends Record<string, string>>(uri: string): Partial<T> {
	let result: Record<string, string> = {};
	let parts = uri.split('?');
	if (parts.length === 1) return result as T;
	for (let entry of parts[1].split('&')) {
		let pair = entry.split('=');
		result[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
	}
	return result as Partial<T>;
}

export function toQuery(params: Record<string, string | number | undefined>): string {
	return Object.keys(params)
		.filter((key) => params[key] !== undefined)
		.map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key]!)}`)
		.join('&');
}

export function useQuery<T extends Record<string, string>>(): Partial<T> {
	return parseQuery<T>(useLocation().search);
}

export function parsePaginationQuery(uri: string, pageSize = 25): { limit: number; offset: number } {
	const params = parseQuery<{ limit: string; offset: string }>(uri);
	return {
		limit: parseInt(params.limit ?? '' + pageSize),
		offset: parseInt(params.offset ?? '0'),
	};
}

type PageParams = { limit: number; offset: number; sort: string };
export function parsePagesQuery(uri: string, pageSize = 24): PageParams {
	const { page, sort: maybeSort } = parseQuery<{ page: string; sort: string }>(uri);
	const sort = maybeSort ?? '';
	const max = page === undefined ? 1 : parseInt(page) || 1;
	return { limit: pageSize, offset: pageSize * (max - 1), sort };
}

type PageParamsLoop54 = {
	limit: number;
	offset: number;
	sort: string;
	price?: string;
	brand?: string;
	inStock?: string;
	campaign?: string;
	isEco?: string;
	bottomCategory?: string;
};
export function parsePagesQueryLoop54(uri: string, pageSize = 24): PageParamsLoop54 {
	const {
		page,
		sort: maybeSort,
		brand,
		price,
		inStock,
		campaign,
		isEco,
		bottomCategory,
	} = parseQuery<{
		page: string;
		sort: string;
		brand: string;
		price: string;
		inStock: string;
		campaign: string;
		isEco: string;
		bottomCategory: string;
	}>(uri);
	const sort = maybeSort ?? '';
	const max = page === undefined ? 1 : parseInt(page) || 1;

	return {
		limit: pageSize,
		offset: pageSize * (max - 1),
		sort,
		brand: brand ?? '',
		price: price ?? '',
		inStock: inStock ?? '',
		campaign: campaign ?? '',
		isEco: isEco ?? '',
		bottomCategory: bottomCategory ?? '',
	};
}

export function useFilterQueryParams(queryStrings: string) {
	const searchParams = new URLSearchParams(queryStrings);
	/*
	 * draftId and draftFile control which draft is rendered; they are not something a listing
	 * filters by. Left in, they reached Loop54 as selected facets named after themselves, and
	 * made hasQueryFilters true on a category page - which switches off the very cms fetch the
	 * preview exists to look at. useCms reads them off the url itself.
	 */
	const excludedParams = ['price', 'brand', 'page', 'draftId', 'draftFile'];
	const filteredParams: Record<string, string> = {};

	searchParams.forEach((value, key) => {
		if (!excludedParams.includes(key)) {
			filteredParams[key] = value;
		}
	});

	return filteredParams;
}

export function usePaginationQuery(pageSize?: number): {
	limit: number;
	offset: number;
} {
	return parsePaginationQuery(useLocation().search, pageSize);
}

type PrivateRouteProps = {
	path: string;
	exact: boolean;
	component: () => JSX.Element;
};
const PrivateRoute = (props: PrivateRouteProps): JSX.Element => {
	const history = useHistory();
	const { isLoggedIn, isLoading } = useSession();
	const urls = useTranslation().routes;

	if (isLoading && !isLoggedIn) return <Main children={null} />; // Blank page to avoid broken grid layout while determining whether to render a private route or redirect.

	const { pathname: previousLocation } = history.location;
	return !isLoggedIn ? <Redirect to={{ pathname: urls.login, state: { previousLocation } }} /> : <Route {...props} />;
};

type Empty = Record<string, never>;
export type Routing<T extends { [K in keyof Empty]?: string } = Empty> = {
	path(i18n: Translations['routes']): string;
	component: () => JSX.Element;
	requiresAuth: boolean;
	/** seconds a rendered page may be served from a cache; the server applies its default when unset */
	cacheMaxAge?: number;
	preload?: (
		config: Config,
		routeMatch: match<T>,
		query: string,
		permaIndexedCategories?: Record<string, ApiCategory>,
		/** null until the market has published one; see hasCmsFile */
		cmsIndex?: CmsIndex | null
	) => Promise<PreloadedData>;
};

export const Routes = (): JSX.Element => {
	const urls = useTranslation().routes;
	return (
		<Switch>
			{routes.map((routing): JSX.Element => {
				const { requiresAuth, component } = routing;
				const path = routing.path(urls);

				return requiresAuth ? (
					<PrivateRoute key={path} path={path} exact component={component} />
				) : (
					<Route key={path} path={path} exact component={component} />
				);
			})}
			<Route component={NotFoundPage} />;
		</Switch>
	);
};

export const routes: Routing<any>[] = [
	homeRouting,
	accountRouting,
	checkoutRouting,
	checkoutThanksRouting,
	companyCheckoutThanksRouting,
	orderFulfillmentRouting,
	ordersRouting,
	orderRouting,
	editOrderRouting,
	exportStatisticsRouting,
	subscriptionsRouting,
	switchAccountRouting,
	reorderRouting,
	createAccountRouting,
	loginRouting,
	favoritesRouting,
	searchRouting,
	tonerGuideRouting,
	printerRouting,
	printerModelRouting,
	brandsPageRouting,
	brandRouting,
	productsAoRouting,
	communicationRouting,
	bonusRouting,
	statisticsRouting,
	previouslyBoughtRouting,
	deleteAccountRouting,
	confirmDeleteRouting,
	campaignRouting,
	resetPasswordRouting,
	forgotPasswordRouting,
	specialPricesRouting,
	specialPricesRouting2,
	myPagesRouting,
	notFoundRouting,
	punchoutRouting,
	// these routes should be last since they match the entire slug
	superCategoryRouting,
	productRouting,
	categoryRouting,
	cmsRouting,
];
